// lib/taskMentions.mjs — R115 C1 (Jesse: "task mentions → in-app notification
// for the tagged person"). Shared by the client (TasksPage @ picker + comment
// rendering) and the server (server/tasks.js resolves who to notify), like
// lib/guestRecovery.mjs — plain JS so `node lib/taskMentions.test.mjs` runs it
// and the CommonJS server import()s it; types in lib/taskMentions.d.mts.
//
// Syntax: `@Full Name` (spaces allowed — the picker inserts full names) or
// `@email` / `@local-part`. A bare unique first name also resolves
// (`@Lacey`), an ambiguous one never does (this roster has three first-name
// collisions). Resolution is greedy longest-match against the active roster,
// case-insensitive; a token ends at the first character that is not part of
// the name (punctuation, newline). A '@' glued to a word (an email address, a
// handle inside a URL) is never a mention. Matches are deduped by email.

// R115 fix (cross 4): who can be mentioned at all. External logins — the
// owner portal, the accounting partner, a vendor login — are never offered by
// the @ picker, never resolved from text, and never counted when deciding
// whether a bare @FirstName is unique (an owner "Lacey X" must not make
// "@Lacey" ambiguous for the cleaner "Lacey Y"). A deny-list, not an
// allow-list: a blank-role staff profile stays mentionable. server/tasks.js
// keeps the same list (NOT_MENTIONABLE; scripts/test_tasks_r115_fixes.js x4.5
// checks they match) — pass mentionRoster(people) to every helper below.
export const MENTION_EXCLUDED_ROLES = ['owner', 'accounting', 'vendor'];
export function mentionRoster(people) {
  return (Array.isArray(people) ? people : []).filter(p => p && !MENTION_EXCLUDED_ROLES.includes(lower(p.role)));
}

const WORD = /[\p{L}\p{N}]/u;
const EMAIL_CHAR = /[\p{L}\p{N}._+\-@]/u;

const str = (v) => (v == null ? '' : String(v));
const lower = (v) => str(v).trim().toLowerCase();

// One alias list per person: full name, email, email local part, and the first
// name when it is unique among the roster. A duplicated full name (two logins
// for the same person) stays matchable but resolves to NO single email —
// `emails[]` carries both so a caller can decide; mentionEmails() skips it.
export function mentionCandidates(people) {
  const list = (Array.isArray(people) ? people : []).filter(p => p && (p.email || p.full_name));
  const firstCount = new Map();   // lowercased first word of every roster name (a one-word name counts too)
  const fullCount = new Map();
  const localCount = new Map();
  const bump = (m, k) => { if (k) m.set(k, (m.get(k) || 0) + 1); };
  for (const p of list) {
    const full = lower(p.full_name);
    if (full && !full.includes('@')) { bump(fullCount, full); bump(firstCount, full.split(/\s+/)[0]); }
    bump(localCount, lower(p.email).split('@')[0]);
  }
  const out = [];
  for (const p of list) {
    const email = lower(p.email);
    const full = str(p.full_name).trim();
    const aliases = [];
    const ownFirst = full && !full.includes('@') ? full.toLowerCase().split(/\s+/)[0] : '';
    if (full && !full.includes('@')) {
      aliases.push({ text: full, kind: 'name', ambiguous: (fullCount.get(full.toLowerCase()) || 0) > 1 });
      const first = full.split(/\s+/)[0];
      if (first && first.toLowerCase() !== full.toLowerCase() && (firstCount.get(first.toLowerCase()) || 0) === 1) aliases.push({ text: first, kind: 'first', ambiguous: false });
    }
    if (email) {
      aliases.push({ text: email, kind: 'email', ambiguous: false });
      // The local part is an alias only when it can't be read as somebody
      // else's first name (kai@… on a roster with two Kais must not resolve
      // `@Kai`) and no other login shares it.
      const local = email.split('@')[0];
      const firstHits = firstCount.get(local) || 0;
      const ownsFirst = firstHits === 1 && ownFirst === local;
      if (local && local.length >= 3 && (localCount.get(local) || 0) === 1 && (firstHits === 0 || ownsFirst)) aliases.push({ text: local, kind: 'local', ambiguous: false });
    }
    out.push({ email, name: full || email, role: str(p.role), aliases });
  }
  return out;
}

function boundaryAfter(text, end, kind) {
  if (end >= text.length) return true;
  const ch = text[end];
  if (kind === 'email' || kind === 'local') return !EMAIL_CHAR.test(ch);
  return !WORD.test(ch);
}
function tokenAround(text, i) {
  let s = i, e = i;
  while (s > 0 && !/\s/.test(text[s - 1])) s--;
  while (e < text.length && !/\s/.test(text[e])) e++;
  return text.slice(s, e);
}
function isMentionStart(text, i) {
  if (text[i] !== '@') return false;
  if (i > 0 && (WORD.test(text[i - 1]) || /[.\-_+@]/.test(text[i - 1]))) return false;   // inside an email / handle
  const tok = tokenAround(text, i);
  if (/:\/\//.test(tok) || /^www\./i.test(tok)) return false;                            // inside a URL
  return true;
}

// parseMentions(text, people) → [{ email, name, start, end, kind, ambiguous?, emails? }]
// start/end index the '@' through the end of the matched alias.
export function parseMentions(text, people) {
  const s = str(text);
  if (!s.includes('@')) return [];
  const cands = mentionCandidates(people);
  const out = [];
  const seen = new Set();
  for (let i = 0; i < s.length; i++) {
    if (!isMentionStart(s, i)) continue;
    const rest = s.slice(i + 1);
    const restLower = rest.toLowerCase();
    let best = null;
    for (const c of cands) {
      for (const a of c.aliases) {
        const al = a.text.toLowerCase();
        if (!al || !restLower.startsWith(al)) continue;
        if (!boundaryAfter(s, i + 1 + al.length, a.kind)) continue;
        if (!best || al.length > best.len) best = { len: al.length, c, a, emails: [c.email] };
        else if (best && al.length === best.len && best.a.kind === 'name' && a.kind === 'name' && !best.emails.includes(c.email)) best.emails.push(c.email);
      }
    }
    if (!best) continue;
    const ambiguous = best.a.ambiguous || best.emails.length > 1;
    const m = { email: ambiguous ? null : best.c.email, name: best.c.name, start: i, end: i + 1 + best.len, kind: best.a.kind };
    if (ambiguous) { m.ambiguous = true; m.emails = best.emails; }
    const key = m.email || `?${m.name.toLowerCase()}`;
    if (!seen.has(key)) { seen.add(key); out.push(m); }
    i = m.end - 1;
  }
  return out;
}

// Deduped lowercase emails of the people a text mentions (ambiguous names skipped).
export function mentionEmails(text, people) {
  return parseMentions(text, people).map(m => m.email).filter(Boolean);
}

// Wrap mentions in the ALREADY-ESCAPED comment text (TasksPage escapes first,
// so a name containing & < > " ' would not match — none on the roster do).
const attr = (v) => str(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
export function renderWithMentions(escapedText, people) {
  const s = str(escapedText);
  const ms = parseMentions(s, people);
  if (!ms.length) return s;
  let out = '', pos = 0;
  for (const m of ms) {
    out += s.slice(pos, m.start);
    out += `<span class="tk-mention"${m.email ? ` data-mention="${attr(m.email)}"` : ''}>${s.slice(m.start, m.end)}</span>`;
    pos = m.end;
  }
  return out + s.slice(pos);
}

// ── picker helpers (client) ────────────────────────────────────────────────
export const MENTION_QUERY_MAX = 40;
// The '@token' the caret is inside, or null. Spaces are allowed in the token
// (full names) but a newline ends it.
export function mentionQuery(text, caret) {
  const s = str(text);
  const c = Math.max(0, Math.min(Number(caret) || 0, s.length));
  const head = s.slice(0, c);
  const at = head.lastIndexOf('@');
  if (at < 0 || !isMentionStart(s, at)) return null;
  const q = head.slice(at + 1);
  if (q.includes('\n') || q.length > MENTION_QUERY_MAX) return null;
  return { start: at, query: q };
}
// People whose name/email start with (then whose any name word starts with,
// then contain) the query; limit rows. Words split on spaces, dashes, dots and
// underscores ("debbie-inspections" is found by "insp").
export function filterPeople(people, query, limit = 8) {
  const q = lower(query);
  const list = (Array.isArray(people) ? people : []).filter(p => p && p.email);
  const score = (p) => {
    const name = lower(p.full_name), email = lower(p.email);
    if (!q) return 2;
    if (name.startsWith(q) || email.startsWith(q)) return 3;
    if (name.split(/[\s\-_.]+/).some(w => w && w.startsWith(q))) return 2;
    if (name.includes(q) || email.includes(q)) return 1;
    return 0;
  };
  return list.map(p => [score(p), p]).filter(([s]) => s > 0).sort((a, b) => b[0] - a[0] || lower(a[1].full_name).localeCompare(lower(b[1].full_name))).slice(0, limit).map(([, p]) => p);
}
// What the picker inserts for a person: the full name, unless that full name
// is shared with another roster entry (two logins for one person) or is
// missing/an email — then the email, which always resolves to exactly one
// person on the server too (descriptions carry no explicit mentions[]).
export function mentionLabel(person, people) {
  const full = str(person && person.full_name).trim();
  const email = lower(person && person.email);
  if (!full || full.includes('@')) return email;
  const n = (Array.isArray(people) ? people : []).filter(p => p && lower(p.full_name) === full.toLowerCase()).length;
  return n > 1 && email ? email : full;
}
// Replace the '@token' (start..caret) with '@Full Name ' and return the new caret.
export function insertMention(text, start, caret, name) {
  const s = str(text);
  const ins = `@${str(name).trim()} `;
  const next = s.slice(0, start) + ins + s.slice(Math.max(start, Number(caret) || 0));
  return { text: next, caret: start + ins.length };
}
// Which of the explicitly picked emails are still named in the text (a
// picked person whose @Name was deleted again must not be notified).
export function keepPicked(text, picked, people) {
  const s = lower(text);
  const cands = mentionCandidates(people);
  const out = [];
  for (const e of (Array.isArray(picked) ? picked : []).map(lower).filter(Boolean)) {
    const c = cands.find(x => x.email === e);
    if (c && c.aliases.some(a => s.includes('@' + a.text.toLowerCase()))) out.push(e);
  }
  return [...new Set(out)];
}
